import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { unprettyVRM } from 'Utilities/formValidators';
import { isNull, isUndefined } from 'Utilities/helpers';

import { Content } from 'LegacySBCRA/Content/Content';
import { VehicleHeader } from 'Storybook/components/local/VehicleHeader/VehicleHeader';
import { useVrmCheckAction } from 'Valuations/Hooks/useVrmCheckAction';

import { stepsConfig } from '../../../../../components/steps/config/howToConfig';
import { StepsSection } from '../../../../../components/steps/StepsSection';
import DialogLoader from '../../../../../components/transitions/DialogLoader/DialogLoader';
import { safeLocalStorage } from '../../../../../utilities/storages';
import USP from '../../../fastlane/home/Usp/Usp';
import { IS_ELECTRIC } from '../../../vehicleProfiling/VehicleProfiling.helpers';
import ComplianceV2 from '../Components/ComplianceV2/ComplianceV2';
import { Demand } from '../Demand/Demand';
import { Emission } from '../Emission/Emission';
import { ULEZ_URL_PATH, ULEZ_VEHICLE_KEY } from '../UlezPage.helpers';
import type { UlezVehicle } from '../UlezPage.types';
import LocalTexts from '../ulezText.json';

import styles from './Results.scss';

const LocalT = new MDText(LocalTexts);
const t = (key: string, props?: { [key:string]: unknown }) => LocalT.translate(key, props) as string;

const Results = () => {
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState<UlezVehicle>();

  const history = useHistory();
  const { vrmCheckAction } = useVrmCheckAction();

  const {
    colour,
    emissions,
    euroStatus,
    isCompliant,
    make,
    model,
    tcc,
    vrmPretty,
  } = vehicle || {};

  useEffect(() => {
    const ulezVehicleData = safeLocalStorage.getItem(ULEZ_VEHICLE_KEY);
    if (ulezVehicleData) {
      setVehicle(JSON.parse(ulezVehicleData));
      setLoading(false);
    } else {
      history.push(ULEZ_URL_PATH);
    }
  }, [history]);

  useEffect(() => {
    if (vrmPretty) {
      // Populate the vehicle lookup context for CVT if the user clicks the CTA
      vrmCheckAction({
        vrmValue: unprettyVRM(vrmPretty),
      });
    }
  }, [vrmCheckAction, vrmPretty]);

  if (loading) {
    return (<DialogLoader />);
  }

  const euroStatusHasValue = !!(euroStatus && euroStatus !== '0');
  const electricCar = vehicle ? IS_ELECTRIC(vehicle) : false;
  const emissionsHaveValue = !!emissions;
  const isNullOrUndefined = isNull(isCompliant) || isUndefined(isCompliant);
  const isEuroStatus3 = !!(euroStatus && euroStatus === '3');

  return (
    <div className={styles.component}>
      <div className={styles.vehicleInner}>
        <Content className={styles.vehicleHeaderContent}>
          <VehicleHeader
            link={{
              label: t('results.changeCar'),
              to: ULEZ_URL_PATH,
            }}
            vehicle={{
              ...vehicle,
              colour: colour ?? tcc?.colour,
              // @ts-ignore
              make: {
                display_name: make?.displayName ?? tcc?.make ?? '',
                slug: make?.slug ?? '',
              },
              model: model ?? tcc?.model ?? '',
              vrm_pretty: vrmPretty,
            }}
          />
        </Content>
      </div>

      <ComplianceV2 {...{
        isCompliant: isCompliant ?? null,
        isEuroStatus3,
        make: make?.displayName ?? tcc?.make,
        vrm: vrmPretty,
      }} />

      {!isNullOrUndefined && (
        <Emission {...{
          electricCar, euroStatusHasValue, showEmissionRating: electricCar || emissionsHaveValue, vehicle,
        }}
        />
      )}

      <Demand
        expandedBlock={!euroStatusHasValue && (isCompliant ?? false)}
        isCompliant={isCompliant}
        vrm={vrmPretty}
      />

      <StepsSection
        config={stepsConfig()}
        title={t('results.howItWorks')}
      />
      <USP className={styles.usp} />
    </div>
  );
};

export default Results;
